import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import api from "../../axiosConfig";

export default function ContacteCreate() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [judetOptions, setJudetOptions] = useState([]);
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [anafLoading, setAnafLoading] = useState(false);
  const [anafError, setAnafError] = useState("");
  const [dataFromAnaf, setDataFromAnaf] = useState(null);
  const ensureRO = (cuiDigits) => (String(cuiDigits || "").startsWith("RO") ? cuiDigits : `RO${cuiDigits}`);
  


  const [form, setForm] = useState({
    // OBLIGATORII
    bxcodfiscal: "", // CUI
    termen_plata_zile: "",
    bxdenumire: "",
    bxadresa: "",
    bxjudet_id: "",
    judet_str: "",
    // OPȚIONALE
    bxoras: "",
    bxcodpostal: "",
    bxtara: "România",
    bxemail: "",
    bxtel: "",
    bxregcom: "",
    bxbanca_nume: "",
    bxbanca_cont: "",
    bxseria: "",
    bxfax: "",
    bxinout_bool: 1,  
    bxcatalog_id: "",
    bxsector_id: "",
  });

  useEffect(() => {
    api
      .get("/judet/", { params: { limit: 100 } })
      .then((res) => setJudetOptions(res.data?.items ?? []))
      .catch(() => setJudetOptions([]));
    api
      .get("/catalog/", { params: { limit: 100 } })
      .then((res) => setCatalogOptions(res.data?.items ?? []))
      .catch(() => setCatalogOptions([]));
  }, []);

  const unaccent = (s = "") =>
    s
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const handleJudetChange = (e) => {
    const id = e.target.value;
    const opt = judetOptions.find((o) => String(o.id) === String(id));
    setForm((f) => ({ ...f, bxjudet_id: id, judet_str: opt?.denumire || "" }));
    if (errors.bxjudet_id)
      setErrors((er) => ({ ...er, bxjudet_id: undefined }));
  };

  // ===== Ia date ANAF după CUI =====
  const fetchFromANAF = async () => {
    setAnafError("");
    setDataFromAnaf(null);

    const cuiRaw = (form.bxcodfiscal || "").toUpperCase().trim();
    const cuiDigits = cuiRaw.replace(/[^0-9]/g, "");
    if (!cuiDigits) {
      setErrors((er) => ({ ...er, bxcodfiscal: "Introduce CUI (ex: RO1234567 sau 1234567)." }));
      return;
    }

    try {
      setAnafLoading(true);
      const { data } = await api.post("/anaf/lookup", { cui: cuiDigits });
      setDataFromAnaf(data);

      setForm((f) => {
        const next = { ...f };

        // CUI normalizat (cu RO)
        if (!f.bxcodfiscal) next.bxcodfiscal = ensureRO(data.cui);

        const tvaYes = data.inregistrare_scop_Tva === "1" || data.scpTVA === true;
        if (!f.bxcodfiscal) {
          next.bxcodfiscal = tvaYes ? `RO${data.cui}` : String(data.cui);
        }

        // Denumire + adresă
        if (!f.bxdenumire && data.denumire) next.bxdenumire = data.denumire;
        if (!f.bxadresa && data.adresa) next.bxadresa = data.adresa;

        // Opționale utile
        if (!f.bxregcom && data.nrRegCom) next.bxregcom = data.nrRegCom;
        if (!f.bxtel && data.telefon) next.bxtel = data.telefon;
        if (data.oras) next.bxoras = data.oras;
        if (data.codPostal) next.bxcodpostal = data.codPostal;
        next.bxtara = "România";

        // Deducere județ (din adresa ANAF; București e special)
        if (!f.bxjudet_id && (data.judet || data.adresa)) {
          const addr = unaccent(`${data.judet || ""} ${data.adresa || ""}`);
          // 1) match exact pe denumire de județ
          let match = judetOptions.find((o) => unaccent(o.denumire || "") && addr.includes(unaccent(o.denumire)));
          // 2) match pe „bucuresti” -> județul Municipiul București
          if (!match && addr.includes("bucuresti")) {
            match = judetOptions.find((o) => unaccent(o.denumire || "").includes("bucuresti"));
          }
          if (match) {
            next.bxjudet_id = String(match.id);
            next.judet_str  = match.denumire || "";
          }
        }

        return next;
      });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setAnafError(`Nu am putut prelua de la ANAF: ${msg}`);
    } finally {
      setAnafLoading(false);
    }
  };

  

  // ===== Validare minimă (obligatorii) =====
  const validate = () => {
    const er = {};
    if (!form.bxdenumire.trim()) er.bxdenumire = "Denumirea este obligatorie.";
    const cui = form.bxcodfiscal.trim();
    if (!cui) er.bxcodfiscal = "CUI este obligatoriu.";
    if (!form.bxadresa.trim()) er.bxadresa = "Adresa este obligatorie.";
    if (!String(form.bxjudet_id)) er.bxjudet_id = "Alege județul.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  // conversii + curățare payload înainte de POST
  const toIntOrUndef = (v) => (v === "" || v == null ? undefined : Number(v));

  const cleanPayload = (obj) => {
    const out = {};
    for (const [k, v] of Object.entries(obj)) if (v !== "" && v != null) out[k] = v;

    // numerice
    ["bxcatalog_id", "bxsector_id", "bxjudet_id"].forEach((k) => {
      if (k in out) out[k] = toIntOrUndef(out[k]);
      if (out[k] === undefined) delete out[k];
    });

    // bxinout_bool doar 1/2
    out.bxinout_bool = [1, 2].includes(Number(out.bxinout_bool)) ? Number(out.bxinout_bool) : 1;

    // nu trimite e_portal
    delete out.e_portal;

    out.termen_plata_zile = obj.termen_plata_zile === "" ? null : Number(obj.termen_plata_zile);
    return out;
  };


  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;

  setSubmitting(true);

  // 👇 disponibil și în catch
  const payload = cleanPayload(form);

  try {
    await api.post("/contacte/", payload);
    navigate("/contacte");
  } catch (err) {
    // Construim un mesaj prietenos pentru 422
    const detail = err?.response?.data;
    let msg = err?.message || "Eroare necunoscută";

    if (Array.isArray(detail)) {
      // FastAPI 422: listă de erori {loc, msg, type}
      msg = detail
        .map((e) => `${(e.loc || []).join(".")}: ${e.msg}`)
        .join("\n");
    } else if (detail?.detail) {
      msg = typeof detail.detail === "string"
        ? detail.detail
        : JSON.stringify(detail.detail);
    }

    console.error("CREATE /contacte error:", {
      status: err?.response?.status,
      data: detail,
      sent: payload, // 👈 acum e definit
    });

    alert("Eroare la creare:\n" + msg);
  } finally {
    setSubmitting(false);
  }
};

  return (
    <Container className="mt-4">
      <Card className="p-3">
        <h3 className="mb-3">Creare contact</h3>
        <Form onSubmit={handleSubmit} noValidate>
          <Row className="g-3">
            {/* 1) CUI + Ia date ANAF */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>
                  CUI <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    name="bxcodfiscal"
                    value={form.bxcodfiscal}
                    onChange={handleChange}
                    placeholder="RO1234567 sau 1234567"
                    isInvalid={!!errors.bxcodfiscal}
                  />
                  <Button
                    variant="outline-primary"
                    onClick={fetchFromANAF}
                    disabled={anafLoading}
                  >
                    {anafLoading ? (
                      <>
                        <Spinner
                          size="sm"
                          animation="border"
                          className="me-1"
                        />{" "}
                        Se caută…
                      </>
                    ) : (
                      "Ia date ANAF"
                    )}
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {errors.bxcodfiscal}
                  </Form.Control.Feedback>
                </InputGroup>
           
                {anafError && (
                  <div className="small text-danger mt-1">{anafError}</div>
                )}
              </Form.Group>
 
            </Col>

            {/* 2) Denumire (obligatoriu) */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>
                  Denumire <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="bxdenumire"
                  value={form.bxdenumire}
                  onChange={handleChange}
                  isInvalid={!!errors.bxdenumire}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.bxdenumire}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* 3) Adresă (obligatoriu) */}
            <Col xs={12}>
              <Form.Group>
                <Form.Label>
                  Adresă <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="bxadresa"
                  value={form.bxadresa}
                  onChange={handleChange}
                  isInvalid={!!errors.bxadresa}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.bxadresa}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* 4) Județ (obligatoriu) */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>
                  Județ <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="bxjudet_id"
                  value={form.bxjudet_id ? String(form.bxjudet_id) : ""}
                  onChange={handleJudetChange}
                  isInvalid={!!errors.bxjudet_id}
                >
                  <option value="">— alege —</option>
                  {judetOptions.map((opt) => (
                    <option key={opt.id} value={String(opt.id)}>
                      {opt.denumire ?? opt.cod ?? opt.id}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.bxjudet_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Oraș</Form.Label>
                <Form.Control
                  name="bxoras"
                  value={form.bxoras}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group controlId="contact-payment-days" className="mb-3"><Form.Label>Termen de plată (zile)</Form.Label><Form.Control type="number" name="termen_plata_zile" min="0" max="3650" step="1" value={form.termen_plata_zile} onChange={handleChange} placeholder="30 implicit"/><Form.Text>Necompletat: 30 de zile. Zero: scadență în ziua facturii.</Form.Text></Form.Group>
                <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="bxemail"
                  value={form.bxemail}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Telefon</Form.Label>
                <Form.Control
                  name="bxtel"
                  value={form.bxtel}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label>Cod poștal</Form.Label>
                <Form.Control
                  name="bxcodpostal"
                  value={form.bxcodpostal}
                  onChange={handleChange}
                  placeholder="ex: 010101"
                  pattern="\d{6}"
                  title="Cod poștal din 6 cifre"
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label>Țara</Form.Label>
                <Form.Control
                  name="bxtara"
                  value={form.bxtara}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label>Reg. Com.</Form.Label>
                <Form.Control
                  name="bxregcom"
                  value={form.bxregcom}
                  onChange={handleChange}
                  placeholder="J00/123/2020"
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Bancă</Form.Label>
                <Form.Control
                  name="bxbanca_nume"
                  value={form.bxbanca_nume}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>IBAN</Form.Label>
                <Form.Control
                  name="bxbanca_cont"
                  value={form.bxbanca_cont}
                  onChange={handleChange}
                  placeholder="RO..."
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label>Tip persoană</Form.Label>
              <Form.Select
                name="bxinout_bool"
                value={form.bxinout_bool}
                onChange={(e) => setForm((f) => ({ ...f, bxinout_bool: Number(e.target.value) }))}
              >
                <option value={1}>1 — Persoană juridică</option>
                <option value={2}>2 — Persoană fizică</option>
              </Form.Select>
            </Form.Group>
          </Col>

            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label>Catalog</Form.Label>
                <Form.Select
                  name="bxcatalog_id"
                  value={form.bxcatalog_id || ""}
                  onChange={handleChange}
                >
                  <option value="">— Fără catalog —</option>
                  {catalogOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.nume_catalog}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label>Sector ID</Form.Label>
                <Form.Control
                  type="number"
                  name="bxsector_id"
                  value={form.bxsector_id}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Serie CI / Doc</Form.Label>
                <Form.Control
                  name="bxseria"
                  value={form.bxseria}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Fax</Form.Label>
                <Form.Control
                  name="bxfax"
                  value={form.bxfax}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="outline-secondary"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              Renunță
            </Button>
            <Button type="submit" variant="dark" disabled={submitting}>
              {submitting ? "Se salvează…" : "Salvează"}
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
}
